import { logger } from '@adornis/base/logging.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { ImageFile } from '@adornis/file-utils/db/images.js';
import { bbxAgentDefaultTemperature, bbxAgentModel, bbxAgentRetryTemperature } from '../../../../shared/models.js';
import { boundingBoxAgentSystemPrompt } from '../../../prompts.js';
import { imagesToBase64URLsWithSizeFallback } from '../../../utils/transform-file.js';
import { getLLMRouter } from '../../llm/llm-gate.js';
import { TaskType } from '../../task-types/task-main.js';

interface RecursiveBoundingBoxNode {
  taskNumber?: string;
  taskType?: string;
  boundingBoxes?: Array<{
    bbox?: { x1: number; y1: number; x2: number; y2: number };
    pageNumber?: number;
    confidence?: number;
  }>;
  dependsOn?: string[];
  subtasks?: RecursiveBoundingBoxNode[];
}

function collectTaskTypesRecursively(nodes: RecursiveBoundingBoxNode[] | undefined, target: Set<TaskType>): void {
  if (!Array.isArray(nodes)) return;
  for (const node of nodes) {
    const rawTaskType = String(node?.taskType ?? '').trim();
    if (Object.values(TaskType).includes(rawTaskType as TaskType)) {
      target.add(rawTaskType as TaskType);
    } else {
      target.add(TaskType.DEFAULT);
    }
    if (Array.isArray(node?.subtasks) && node.subtasks.length > 0) {
      collectTaskTypesRecursively(node.subtasks, target);
    }
  }
}

function extractTaskTypesFromStructure(jsonStruct: string): TaskType[] {
  try {
    const parsed = JSON.parse(jsonStruct);
    const taskTypes = new Set<TaskType>();
    collectTaskTypesRecursively(parsed?.tasks as RecursiveBoundingBoxNode[] | undefined, taskTypes);
    if (taskTypes.size === 0) taskTypes.add(TaskType.DEFAULT);
    return Array.from(taskTypes);
  } catch (error) {
    logger.warn(
      { err: error, context: 'BBX' },
      'Failed to parse structure JSON for taskType collection. Falling back to DEFAULT.',
    );
    return [TaskType.DEFAULT];
  }
}

function isValidBoundingBox(value: any): boolean {
  return (
    !!value &&
    typeof value.bbox?.x1 === 'number' &&
    typeof value.bbox?.y1 === 'number' &&
    typeof value.bbox?.x2 === 'number' &&
    typeof value.bbox?.y2 === 'number' &&
    typeof value.pageNumber === 'number' &&
    typeof value.confidence === 'number'
  );
}

function isValidRecursiveBoundingBoxNode(node: unknown): boolean {
  if (!node || typeof node !== 'object') return false;
  const typedNode = node as RecursiveBoundingBoxNode;
  if (typeof typedNode.taskNumber !== 'string' || typedNode.taskNumber.trim().length === 0) return false;
  if (!Array.isArray(typedNode.boundingBoxes)) return false;
  if (typedNode.boundingBoxes.some(bbox => !isValidBoundingBox(bbox))) return false;
  if (typedNode.dependsOn && !Array.isArray(typedNode.dependsOn)) return false;
  if (!Array.isArray(typedNode.subtasks)) return false;
  return typedNode.subtasks.every(child => isValidRecursiveBoundingBoxNode(child));
}

function isValidAdditionalInformationEntry(entry: any): boolean {
  if (!entry || typeof entry.id !== 'string' || !entry.region || !isValidBoundingBox(entry.region)) return false;
  if (entry.dependsOn !== undefined && !Array.isArray(entry.dependsOn)) return false;
  if (entry.maxPoints !== undefined && typeof entry.maxPoints !== 'number') return false;
  return true;
}

// Language-use rule text may span multiple pages (unlike additionalInformation's single "region"), so it
// carries a "regions" array — one bounding box per page — instead.
function isValidLanguageUseRuleEntry(entry: any): boolean {
  if (!entry || typeof entry.id !== 'string') return false;
  if (!Array.isArray(entry.regions) || entry.regions.length === 0) return false;
  if (!entry.regions.every((region: unknown) => isValidBoundingBox(region))) return false;
  if (entry.dependsOn !== undefined && !Array.isArray(entry.dependsOn)) return false;
  if (entry.maxPoints !== undefined && typeof entry.maxPoints !== 'number') return false;
  return true;
}

// The model occasionally wraps the result in a top-level array (e.g. [{ studentName, tasks }])
// instead of returning the expected object ({ tasks: [...] }). Unwrap it so validation and
// downstream consumers always see the object shape. Merges tasks if multiple objects are wrapped.
function normalizeBoundingBoxAnswer(answer: string): string {
  try {
    const parsed = JSON.parse(answer);
    if (!Array.isArray(parsed)) return answer;

    const tasks = parsed.flatMap((entry: any) => (Array.isArray(entry?.tasks) ? entry.tasks : []));
    const additionalInformation = parsed.flatMap((entry: any) =>
      Array.isArray(entry?.additionalInformation) ? entry.additionalInformation : [],
    );
    const languageUseRules = parsed.flatMap((entry: any) =>
      Array.isArray(entry?.languageUseRules) ? entry.languageUseRules : [],
    );

    const normalized: Record<string, unknown> = { tasks };
    if (additionalInformation.length > 0) normalized.additionalInformation = additionalInformation;
    if (languageUseRules.length > 0) normalized.languageUseRules = languageUseRules;
    return JSON.stringify(normalized);
  } catch {
    return answer;
  }
}

function isValidBoundingBoxResult(answer: string): boolean {
  try {
    const parsed = JSON.parse(answer);
    if (!Array.isArray(parsed?.tasks) || parsed.tasks.length === 0) return false;
    if (!parsed.tasks.every((task: unknown) => isValidRecursiveBoundingBoxNode(task))) return false;
    if (parsed.additionalInformation !== undefined) {
      if (!Array.isArray(parsed.additionalInformation)) return false;
      if (!parsed.additionalInformation.every((entry: unknown) => isValidAdditionalInformationEntry(entry)))
        return false;
    }
    // Optional (GS pass only): globally applicable language-use rules, same entry shape as
    // additionalInformation. Tolerated as absent — models may omit the key entirely.
    if (parsed.languageUseRules !== undefined) {
      if (!Array.isArray(parsed.languageUseRules)) return false;
      if (!parsed.languageUseRules.every((entry: unknown) => isValidLanguageUseRuleEntry(entry))) return false;
    }
    return true;
  } catch {
    return false;
  }
}

async function runBoundingBoxWithValidation(
  contentParts: any[],
  systemPrompt: string,
  maxRetries = 1,
): Promise<{ answer: string; cost: number }> {
  let retryCount = 0;
  let lastCost = 0;

  while (retryCount <= maxRetries) {
    const router = getLLMRouter();
    const temperature = retryCount > 0 ? bbxAgentRetryTemperature : bbxAgentDefaultTemperature;

    const imageParts = contentParts.filter((p: any) => p.type === 'image_url');
    const textParts = contentParts.filter((p: any) => p.type === 'text');
    let totalBase64Bytes = 0;
    for (const part of imageParts) {
      const url: string = part.image_url?.url ?? '';
      const base64Part = (url.includes(',') ? url.split(',')[1] : url) ?? '';
      totalBase64Bytes += base64Part.length;
    }
    logger.info(
      {
        attempt: retryCount + 1,
        imageCount: imageParts.length,
        textPartCount: textParts.length,
        payloadMB: (totalBase64Bytes / 1024 / 1024).toFixed(2),
        context: 'BBX',
      },
      'Sending images and text parts in request body',
    );

    try {
      const response = await router.chat.completions.create({
        model: bbxAgentModel,
        provider: 'openrouter',
        temperature,
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: contentParts },
        ],
        response_format: { type: 'json_object' },
      });

      const usage = response.usage;
      const usageCost = Number((usage as any)?.cost ?? 0);
      lastCost += usageCost;
      logger.info({ usageCost: JSON.stringify(usageCost), context: 'COST_TRACKING' }, 'BBX Agent cost');

      if (!response.choices || !Array.isArray(response.choices) || !response.choices[0]?.message?.content) {
        logger.warn({ attempt: retryCount + 1 }, 'BBX attempt: No valid choices/content in LLM response.');
        retryCount++;
        continue;
      }

      const rawContent = response.choices[0].message.content;
      const rawAnswer = typeof rawContent === 'string' ? rawContent : JSON.stringify(rawContent ?? '');

      const finalAnswer = normalizeBoundingBoxAnswer(rawAnswer);

      if (isValidBoundingBoxResult(finalAnswer)) {
        logger.info({ attempt: retryCount + 1 }, 'BBX validation passed on attempt');
        return { answer: finalAnswer, cost: lastCost };
      }

      logger.warn({ attempt: retryCount + 1 }, 'BBX validation failed on attempt');
    } catch (error) {
      logger.error(
        { err: error, attempt: retryCount + 1, context: 'BBX' },
        'Router connection encountered a failure on attempt',
      );
    }

    retryCount++;
  }

  throw new Error(
    'Bounding box detection failed after multiple attempts. Please check the input images and structure, and try again.',
  );
}

async function fetchImagesAsBase64(imageIds: string[], logTag: string): Promise<Array<{ id: string; url: string }>> {
  const images: any[] = [];
  for (const imageId of imageIds) {
    if (!imageId) continue;
    const image = await getByID(ImageFile, imageId)(ImageFile.allFields);
    if (image) {
      images.push(image);
    } else {
      logger.info({ imageId }, 'Image not found');
    }
  }
  if (images.length === 0) return [];

  const imageIDsAfter = images.map(img => img._id);
  logger.info({ imageCount: images.length, imageIDs: imageIDsAfter.join(', '), context: 'BBX' }, 'Fetched images');

  const uniqueIds = new Set(imageIDsAfter);
  if (uniqueIds.size !== imageIDsAfter.length) {
    logger.warn(
      { totalCount: imageIDsAfter.length, uniqueCount: uniqueIds.size, context: 'BBX' },
      'WARNING: duplicate image IDs detected',
    );
  }

  const getImageFile = (img: any) =>
    img instanceof ImageFile
      ? img
      : new ImageFile({
          ...img,
          meta: img.meta ?? {},
          createdAt: img.createdAt ?? new Date(),
          _id: img._id ?? '',
          _class: img._class ?? ImageFile._class,
        });

  const imageBase64urls = await imagesToBase64URLsWithSizeFallback(
    images.map((img: any) => getImageFile(img)),
    { logTag },
  );

  return images
    .map((img, i) => ({ id: String(img._id), url: imageBase64urls[i] }))
    .filter((entry): entry is { id: string; url: string } => !!entry.url);
}

export async function runBoundingBoxAgent(
  imageIds: string[],
  bbxKind: any,
  structure: any,
  /**
   * Extra images that are NOT exam pages (e.g. diagrams from `Exam.additionalInformation`), each tagged with
   * the reference id a task's `additionalInfoIds` uses to point at it. Attached as a separate labeled block
   * after the exam pages so page numbering/bounding-box detection for the exam pages is unaffected.
   */
  referenceImages?: Array<{ id: string; imageId: string }>,
): Promise<{ answer: string; cost: number } | null> {
  try {
    if (!imageIds || imageIds.length === 0) {
      throw new Error('No image IDs provided');
    }

    const primaryImages = await fetchImagesAsBase64(imageIds, '[BBX]');
    if (primaryImages.length === 0) {
      throw new Error('No images found for provided IDs');
    }

    logger.info({ imageCount: primaryImages.length, bbxKind, context: 'BBX' }, 'Processing images for bbxKind');

    const contentParts: any[] = [
      {
        type: 'text',
        text: `Analyse the Layout of these exam pages (${primaryImages.length} Page(s)) and return the regions for this structure: ${structure}.\nThe images are the pages of the exam in order.`,
      },
      ...primaryImages.map(({ url }) => ({ type: 'image_url', image_url: { url, detail: 'low' } })),
    ];

    const validReferenceImages = (referenceImages ?? []).filter(ref => ref?.id && ref?.imageId);
    if (validReferenceImages.length > 0) {
      const resolved = await fetchImagesAsBase64(
        validReferenceImages.map(ref => ref.imageId),
        '[BBX-REF]',
      );
      const urlByImageId = new Map(resolved.map(entry => [entry.id, entry.url]));
      const attached = validReferenceImages
        .map(ref => ({ id: ref.id, url: urlByImageId.get(ref.imageId) }))
        .filter((entry): entry is { id: string; url: string } => !!entry.url);

      if (attached.length > 0) {
        logger.info({ referenceImageCount: attached.length, context: 'BBX' }, 'Attaching reference images');
        contentParts.push({
          type: 'text',
          text:
            `Additionally, ${attached.length} reference image(s) are attached below. These are NOT exam pages — ` +
            `do not create bounding boxes or page numbers for them. Each is preceded by its reference id; a ` +
            `task's "additionalInfoIds" field in the structure above lists which reference id(s) apply to it. If an additionalInfoId is not present in the task's additionalInfoIds field, ignore that reference image for that task.`,
        });
        for (const { id, url } of attached) {
          contentParts.push({ type: 'text', text: `Reference image [id="${id}"]:` });
          contentParts.push({ type: 'image_url', image_url: { url, detail: 'low' } });
        }
      }
    }

    const taskTypes = extractTaskTypesFromStructure(structure);
    const systemPrompt =
      boundingBoxAgentSystemPrompt(bbxKind as any, taskTypes) ||
      'You are a helpful assistant for extracting bounding boxes from exam pages.';

    const result = await runBoundingBoxWithValidation(contentParts, systemPrompt, 1);
    logger.info('Bounding Box Agent - Antwort erhalten und validiert');
    return result;
  } catch (err) {
    logger.error({ err, imageIds: imageIds.join(', ') }, 'Bounding box detection failed for image IDs');
    return null;
  }
}
